import type { ReactNode } from 'react';

export interface OrderableItem {
  id: string;
  title: string;
  subtitle?: string;
  status: 'draft' | 'live';
  href: string;
  thumbnailUrl?: string;
}

/**
 * Ordering is persisted through a single `reorder` call after the move, so a
 * long list never turns into a burst of N writes.
 */
export function OrderableList({
  items,
  onReorder,
  onDelete,
  canDelete,
  emptyMessage,
  renderLink,
}: {
  items: OrderableItem[];
  onReorder: (ids: string[]) => void;
  onDelete?: (id: string) => void;
  canDelete: boolean;
  emptyMessage: string;
  renderLink: (item: OrderableItem, children: ReactNode) => ReactNode;
}) {
  if (items.length === 0) return <p className="empty-note">{emptyMessage}</p>;

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const ids = items.map((i) => i.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(target, 0, moved!);
    onReorder(ids);
  };

  return (
    <ul className="orderable-list">
      {items.map((item, index) => (
        <li key={item.id} className="orderable-item">
          <div className="orderable-move">
            <button
              type="button"
              className="btn icon"
              aria-label={`Move ${item.title} up`}
              disabled={index === 0}
              onClick={() => move(index, -1)}
            >
              ↑
            </button>
            <button
              type="button"
              className="btn icon"
              aria-label={`Move ${item.title} down`}
              disabled={index === items.length - 1}
              onClick={() => move(index, 1)}
            >
              ↓
            </button>
          </div>

          {item.thumbnailUrl ? (
            <img className="orderable-thumb" src={item.thumbnailUrl} alt="" loading="lazy" />
          ) : (
            <div className="orderable-thumb empty" aria-hidden="true" />
          )}

          <div className="orderable-body">
            {renderLink(
              item,
              <>
                <span className="orderable-title">{item.title}</span>
                {item.subtitle ? <span className="orderable-sub">{item.subtitle}</span> : null}
              </>,
            )}
          </div>

          <StatusBadge status={item.status} />

          {canDelete && onDelete ? (
            <button
              type="button"
              className="btn icon danger"
              aria-label={`Delete ${item.title}`}
              onClick={() => onDelete(item.id)}
            >
              ×
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function StatusBadge({ status }: { status: 'draft' | 'live' }) {
  return <span className={`badge ${status}`}>{status === 'live' ? 'Live' : 'Draft'}</span>;
}
